import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Chip } from '@mui/material';
import {
  Work,
  AttachMoney,
  Schedule,
  Star,
  CheckCircle,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { brand, text, border } from '../../theme/colors';

/**
 * Enhanced Stat Cards with Gradients and Visual Appeal
 * Inspired by modern dashboard designs
 */
const EnhancedContractorStatCards = ({ stats, trends }) => {
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getTrendIcon = (trend) => {
    if (!trend || trend === 0) return null;
    return trend > 0 ? <TrendingUp sx={{ fontSize: 16 }} /> : <TrendingDown sx={{ fontSize: 16 }} />;
  };

  const getTrendColor = (trend) => {
    if (!trend || trend === 0) return '#666';
    return trend > 0 ? '#43B581' : '#F04747';
  };

  const statCards = [
    {
      title: 'Active Projects',
      value: stats.activeProjects || 0,
      icon: <Work sx={{ fontSize: 40, opacity: 0.3, color: brand.primary }} />,
      gradient: 'white',
      color: brand.primary,
      trend: trends?.activeProjects || 0,
      trendLabel: 'vs last month'
    },
    {
      title: 'Total Earnings',
      value: formatCurrency(stats.totalEarnings || 0),
      icon: <AttachMoney sx={{ fontSize: 40, opacity: 0.3, color: '#43B581' }} />,
      gradient: 'white',
      color: '#43B581',
      trend: trends?.earnings || 0,
      trendLabel: 'vs last month'
    },
    {
      title: 'Pending Payments',
      value: formatCurrency(stats.pendingPayments || 0),
      icon: <Schedule sx={{ fontSize: 40, opacity: 0.3, color: '#FAA61A' }} />,
      gradient: 'white',
      color: '#FAA61A',
      trend: trends?.pendingPayments || 0,
      trendLabel: 'awaiting release'
    },
    {
      title: 'Rating',
      value: `${stats.rating || 0}`,
      icon: <Star sx={{ fontSize: 40, opacity: 0.3, color: brand.secondary }} />,
      gradient: 'white',
      color: brand.secondary,
      subtitle: `${stats.reviewCount || 0} reviews`,
      showStar: true
    },
    {
      title: 'Completed Projects',
      value: stats.completedProjects || 0,
      icon: <CheckCircle sx={{ fontSize: 40, opacity: 0.3, color: '#16a085' }} />,
      gradient: 'white',
      color: '#16a085',
      trend: trends?.completedProjects || 0,
      trendLabel: 'this month'
    }
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {statCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
          <Card
            sx={{
              backgroundColor: 'white',
              color: text.primary,
              border: `2px solid ${border.light}`,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                borderColor: text.primary
              }
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              {/* Background Icon */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10
                }}
              >
                {card.icon}
              </Box>

              {/* Title */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: card.color,
                  fontSize: '0.875rem'
                }}
              >
                {card.title}
              </Typography>

              {/* Value */}
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 0.5,
                  fontSize: { xs: '1.75rem', md: '2rem' },
                  color: card.color
                }}
              >
                {card.value}
                {card.showStar && ' ⭐'}
              </Typography>

              {/* Subtitle or Trend */}
              {card.subtitle && (
                <Typography
                  variant="caption"
                  sx={{
                    color: text.secondary,
                    fontSize: '0.75rem'
                  }}
                >
                  {card.subtitle}
                </Typography>
              )}

              {Number.isFinite(card.trend) && card.trend !== 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 1
                  }}
                >
                  <Chip
                    size="small"
                    icon={getTrendIcon(card.trend)}
                    label={`${card.trend > 0 ? '+' : ''}${card.trend}%`}
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: '#f5f5f5',
                      color: getTrendColor(card.trend),
                      border: `1px solid ${border.light}`,
                      '& .MuiChip-icon': {
                        color: getTrendColor(card.trend)
                      }
                    }}
                  />
                  {card.trendLabel && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: text.secondary,
                        fontSize: '0.7rem'
                      }}
                    >
                      {card.trendLabel}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default EnhancedContractorStatCards;
